import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  SupervisorAccount as SupervisorIcon,
  People as TherapistsIcon,
  Assignment as ConsultationsIcon,
  Note as NotesIcon,
  TrendingUp as TrendingIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowIcon,
  AssignmentTurnedIn as TriageIcon,
  Notifications as AlertsIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Shield as ShieldIcon,
  School as SimulationsIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';
import api from '../../services/api';
import SignupLinksWidget from './SignupLinksWidget';

function ClinicalSupervisorDashboard({ token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await clinicalSupervisorApi.getDashboard(token);

      // Fetch pending triage count
      try {
        const triageResponse = await api.get('/triage/pending');
        data.pendingTriageCount = triageResponse.data?.count || 0;
      } catch (triageErr) {
        console.error('Error loading triage count:', triageErr);
        data.pendingTriageCount = 0;
      }

      // Fetch awaiting-release practice sessions count for the badge.
      try {
        const psRes = await api.get('/simulation-governance/supervisor/practice-sessions?released=awaiting');
        data.awaitingPracticeSessions = psRes?.data?.counts?.awaiting || 0;
      } catch (_) {
        data.awaitingPracticeSessions = 0;
      }

      setDashboardData(data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.response?.data?.msg || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [token]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={loadDashboard} startIcon={<RefreshIcon />} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  const { supervisorInfo, therapists, consultationStats, pendingCareNotes, recentActivity, pendingTriageCount, alertStats, recentAlerts, awaitingPracticeSessions } =
    dashboardData || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom display="flex" alignItems="center" gap={1}>
            <SupervisorIcon fontSize="large" color="primary" />
            Clinical Supervisor Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and oversee your assigned therapists
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadDashboard}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <TherapistsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">{therapists?.length || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assigned Therapists
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/clinical-supervisor/therapists')}
              >
                View All
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <ConsultationsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">
                    {consultationStats?.activeConsultations || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Consultations
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Total: {consultationStats?.totalConsultations || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <NotesIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">{pendingCareNotes?.length || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Care Notes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/clinical-supervisor/care-notes/pending')}
              >
                Review
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <TriageIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">{pendingTriageCount || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Triages
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/clinical-supervisor/triage')}
              >
                Review
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            bgcolor: (alertStats?.criticalCount > 0) ? 'error.light' : 'background.paper',
            border: (alertStats?.criticalCount > 0) ? 2 : 0,
            borderColor: 'error.main'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: (alertStats?.criticalCount > 0) ? 'error.main' : 'warning.main', mr: 2 }}>
                  {(alertStats?.criticalCount > 0) ? <WarningIcon /> : <AlertsIcon />}
                </Avatar>
                <Box>
                  <Typography variant="h4">{alertStats?.totalActive || 0}</Typography>
                  <Typography variant="body2" color={(alertStats?.criticalCount > 0) ? 'error.dark' : 'text.secondary'}>
                    Active Alerts
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                {alertStats?.criticalCount > 0 && (
                  <Chip
                    label={`${alertStats.criticalCount} Critical`}
                    color="error"
                    size="small"
                  />
                )}
                {alertStats?.urgentCount > 0 && (
                  <Chip
                    label={`${alertStats.urgentCount} Urgent`}
                    color="warning"
                    size="small"
                  />
                )}
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/clinical-supervisor/alerts')}
                color={(alertStats?.criticalCount > 0) ? 'error' : 'primary'}
              >
                View All
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <TrendingIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">
                    {consultationStats?.completedThisMonth || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed This Month
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/clinical-supervisor/reports')}
              >
                View Reports
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'grey.600', mr: 2 }}>
                  <ScheduleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Service Hours</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage group schedules
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/clinical-supervisor/service-hours')}
              >
                Configure
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <ShieldIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Boundary Monitoring</Typography>
                  <Typography variant="body2" color="text.secondary">
                    CBC & Parasocial detection
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/clinical-supervisor/boundary-monitoring')}
              >
                View Dashboard
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <SimulationsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Simulations</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Per-therapist scenario grants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/clinical-supervisor/simulations')}
              >
                Manage Grants
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: (awaitingPracticeSessions || 0) > 0 ? 'warning.main' : 'success.main', mr: 2 }}>
                  <SimulationsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4">{awaitingPracticeSessions || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Practice Sessions
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    awaiting release
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/clinical-supervisor/practice-sessions')}
              >
                Review queue
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Therapists List */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Your Therapists
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {therapists && therapists.length > 0 ? (
              <List>
                {therapists.slice(0, 5).map((therapist) => (
                  <ListItem
                    key={therapist._id}
                    button
                    onClick={() => navigate(`/clinical-supervisor/therapists/${therapist._id}`)}
                  >
                    <ListItemAvatar>
                      <Avatar>{therapist.name?.[0]?.toUpperCase() || 'T'}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={therapist.name}
                      secondary={`${therapist.therapistInfo?.assignedClientCount || 0} clients`}
                    />
                    <Chip
                      label={therapist.status}
                      size="small"
                      color={therapist.status === 'Active' ? 'success' : 'default'}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                No therapists assigned yet
              </Typography>
            )}
            {therapists && therapists.length > 5 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Button onClick={() => navigate('/clinical-supervisor/therapists')}>
                  View All ({therapists.length})
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Pending Care Notes */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Pending Care Note Reviews
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {pendingCareNotes && pendingCareNotes.length > 0 ? (
              <List>
                {pendingCareNotes.slice(0, 5).map((item) => (
                  <ListItem
                    key={`${item.consultationId}-${item.noteId}`}
                    button
                    onClick={() =>
                      navigate(`/clinical-supervisor/care-notes/${item.consultationId}/${item.noteId}`)
                    }
                  >
                    <ListItemText
                      primary={`${item.therapist.name} - ${item.client.name}`}
                      secondary={`${item.note.type} • ${item.note.priority} priority`}
                    />
                    <Chip
                      label={item.note.pendingNotes || 'Review'}
                      size="small"
                      color="warning"
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                No pending care notes
              </Typography>
            )}
            {pendingCareNotes && pendingCareNotes.length > 5 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Button onClick={() => navigate('/clinical-supervisor/care-notes/pending')}>
                  View All ({pendingCareNotes.length})
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Critical/Urgent Alerts */}
        {recentAlerts && recentAlerts.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: 'warning.lighter', border: 1, borderColor: 'warning.main' }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WarningIcon color="error" />
                <Typography variant="h6">
                  Recent Critical/Urgent Alerts
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List>
                {recentAlerts.map((alert) => {
                  const therapistId = alert.therapistId?._id || alert.therapistId;
                  const clientId = alert.clientId?._id || alert.clientId;

                  return (
                  <ListItem
                    key={alert._id}
                    button
                    onClick={() => {
                      if (therapistId && clientId) {
                        navigate(`/clinical-supervisor/therapists/${therapistId}/clients/${clientId}`);
                      }
                    }}
                    sx={{
                      bgcolor: 'background.paper',
                      mb: 1,
                      borderRadius: 1,
                      border: 1,
                      borderColor: alert.priority === 'critical' ? 'error.main' : 'warning.main',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: alert.priority === 'critical' ? 'error.main' : 'warning.main' }}>
                        <WarningIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle1">
                            {alert.clientId?.name || 'Unknown Client'}
                          </Typography>
                          <Chip
                            label={alert.priority?.toUpperCase()}
                            size="small"
                            color={alert.priority === 'critical' ? 'error' : 'warning'}
                          />
                          {alert.metadata?.crisisType && (
                            <Chip
                              label={alert.metadata.crisisType.replace(/_/g, ' ')}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Therapist: {alert.therapistId?.name || 'Unknown'} • {new Date(alert.createdAt).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {alert.concern?.explanation || alert.metadata?.explanation || 'No details available'}
                          </Typography>
                        </Box>
                      }
                    />
                    <Box>
                      <Chip
                        label={alert.status}
                        size="small"
                        color={alert.status === 'pending' ? 'error' : 'default'}
                      />
                    </Box>
                  </ListItem>
                  );
                })}
              </List>
              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  variant="contained"
                  color={alertStats?.criticalCount > 0 ? 'error' : 'warning'}
                  endIcon={<ArrowIcon />}
                  onClick={() => navigate('/clinical-supervisor/alerts')}
                >
                  View All Alerts ({alertStats?.totalActive || 0})
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Signup Links Widget */}
        <Grid item xs={12}>
          <SignupLinksWidget token={token} />
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {recentActivity && recentActivity.length > 0 ? (
              <List>
                {recentActivity.map((event, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={event.eventType.replace(/_/g, ' ')}
                      secondary={new Date(event.timestamp).toLocaleString()}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                No recent activity
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ClinicalSupervisorDashboard;
